/* ==========================================================================
   LE SOUFFLE DES HÉROS — Comportements du thème
   --------------------------------------------------------------------------
   Aucune dépendance externe. Tout repose sur les API natives du navigateur :
   IntersectionObserver pour les apparitions, fetch pour le panier, Custom
   Elements pour les composants. Poids total sous 8 ko.
   ========================================================================== */

(function () {
  'use strict';

  const reduitLeMouvement = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     Apparitions au défilement
     ------------------------------------------------------------------------
     Un seul observateur pour toute la page. On cesse d'observer dès qu'un
     élément est apparu : il n'y a aucune raison de le suivre ensuite.
     ---------------------------------------------------------------------- */

  function initApparitions() {
    const cibles = document.querySelectorAll('[data-apparition]');
    if (!cibles.length) return;

    if (reduitLeMouvement.matches || !('IntersectionObserver' in window)) {
      cibles.forEach((el) => el.classList.add('est-visible'));
      return;
    }

    const observateur = new IntersectionObserver(
      (entrees) => {
        entrees.forEach((entree) => {
          if (!entree.isIntersecting) return;
          entree.target.classList.add('est-visible');
          observateur.unobserve(entree.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );

    cibles.forEach((el) => observateur.observe(el));
  }

  /* ------------------------------------------------------------------------
     Parallaxe du visuel d'accueil
     ------------------------------------------------------------------------
     Désactivée sous 768 px et si le mouvement est réduit. On lit la position
     de défilement dans requestAnimationFrame pour ne jamais forcer un
     recalcul de mise en page pendant le scroll.
     ---------------------------------------------------------------------- */

  function initParallaxe() {
    const media = document.querySelector('[data-parallaxe]');
    if (!media) return;

    const grandEcran = window.matchMedia('(min-width: 48rem)');
    let enAttente = false;

    function appliquer() {
      const decalage = Math.min(window.scrollY, window.innerHeight) * 0.28;
      media.style.transform = 'translate3d(0, ' + decalage + 'px, 0)';
      enAttente = false;
    }

    function surDefilement() {
      if (enAttente) return;
      enAttente = true;
      window.requestAnimationFrame(appliquer);
    }

    function evaluer() {
      const actif = grandEcran.matches && !reduitLeMouvement.matches;
      window.removeEventListener('scroll', surDefilement);
      if (actif) {
        window.addEventListener('scroll', surDefilement, { passive: true });
        appliquer();
      } else {
        media.style.transform = '';
      }
    }

    evaluer();
    grandEcran.addEventListener('change', evaluer);
    reduitLeMouvement.addEventListener('change', evaluer);
  }

  /* ------------------------------------------------------------------------
     Tiroir de navigation
     ------------------------------------------------------------------------
     Piège à focus, fermeture à Échap, restitution du focus au déclencheur.
     ---------------------------------------------------------------------- */

  class TiroirNavigation extends HTMLElement {
    connectedCallback() {
      this.declencheur = document.querySelector('[data-ouvre-tiroir]');
      this.fermeture = this.querySelector('[data-ferme-tiroir]');
      this.voile = this.querySelector('.tiroir__voile');
      this.panneau = this.querySelector('.tiroir__panneau');

      if (this.declencheur) {
        this.declencheur.addEventListener('click', () => this.ouvrir());
      }
      if (this.fermeture) {
        this.fermeture.addEventListener('click', () => this.fermer());
      }
      if (this.voile) {
        this.voile.addEventListener('click', () => this.fermer());
      }

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.fermer();
        } else if (e.key === 'Tab') {
          this.piegerFocus(e);
        }
      });
    }

    ouvrir() {
      this.classList.add('est-ouvert');
      this.setAttribute('aria-hidden', 'false');
      if (this.declencheur) this.declencheur.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      const premier = this.focusables()[0];
      if (premier) window.requestAnimationFrame(() => premier.focus());
    }

    fermer() {
      this.classList.remove('est-ouvert');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      if (this.declencheur) {
        this.declencheur.setAttribute('aria-expanded', 'false');
        this.declencheur.focus();
      }
    }

    focusables() {
      return Array.from(
        this.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
    }

    piegerFocus(e) {
      const elements = this.focusables();
      if (!elements.length) return;

      const premier = elements[0];
      const dernier = elements[elements.length - 1];

      if (e.shiftKey && document.activeElement === premier) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && document.activeElement === dernier) {
        e.preventDefault();
        premier.focus();
      }
    }
  }

  customElements.define('tiroir-navigation', TiroirNavigation);

  /* ------------------------------------------------------------------------
     Sélecteur de quantité
     ---------------------------------------------------------------------- */

  class SelecteurQuantite extends HTMLElement {
    connectedCallback() {
      this.saisie = this.querySelector('input');
      if (!this.saisie) return;

      this.querySelectorAll('button').forEach((bouton) => {
        bouton.addEventListener('click', () => {
          const pas = bouton.dataset.pas === 'moins' ? -1 : 1;
          const min = parseInt(this.saisie.min || '1', 10);
          const valeur = Math.max(min, (parseInt(this.saisie.value, 10) || min) + pas);
          this.saisie.value = valeur;
          this.saisie.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  customElements.define('selecteur-quantite', SelecteurQuantite);

  /* ------------------------------------------------------------------------
     Sélecteur de variantes
     ------------------------------------------------------------------------
     Met à jour l'URL, le prix, la disponibilité et le bouton d'achat sans
     recharger la page. L'état final est toujours écrit explicitement : on ne
     dépend jamais d'un événement de fin d'animation pour être correct.
     ---------------------------------------------------------------------- */

  class SelecteurVariantes extends HTMLElement {
    connectedCallback() {
      this.formulaire = this.closest('form');
      this.donnees = this.lireDonnees();
      this.addEventListener('change', () => this.actualiser());
    }

    lireDonnees() {
      const script = this.querySelector('[type="application/json"]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return [];
      }
    }

    optionsChoisies() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked')).map((i) => i.value);
    }

    actualiser() {
      const choix = this.optionsChoisies();
      const variante = this.donnees.find((v) =>
        v.options.every((option, index) => option === choix[index])
      );

      this.majBouton(variante);
      this.majPrix(variante);
      this.majUrl(variante);
      this.majChampCache(variante);
    }

    majChampCache(variante) {
      const champ = this.formulaire && this.formulaire.querySelector('input[name="id"]');
      if (champ && variante) champ.value = variante.id;
    }

    majBouton(variante) {
      const bouton = this.formulaire && this.formulaire.querySelector('[data-ajouter]');
      if (!bouton) return;

      const libelle = bouton.querySelector('.bouton__libelle');
      const indisponible = !variante || !variante.available;

      bouton.disabled = indisponible;
      bouton.setAttribute('aria-disabled', String(indisponible));

      if (libelle) {
        libelle.textContent = !variante
          ? bouton.dataset.libelleIndisponible
          : variante.available
            ? bouton.dataset.libelleDefaut
            : bouton.dataset.libelleEpuise;
      }
    }

    majPrix(variante) {
      const zone = document.querySelector('[data-prix-produit]');
      if (!zone || !variante) return;

      const format = (centimes) =>
        new Intl.NumberFormat(document.documentElement.lang || 'fr', {
          style: 'currency',
          currency: zone.dataset.devise || 'EUR'
        }).format(centimes / 100);

      let html = '<span class="chiffres">' + format(variante.price) + '</span>';
      if (variante.compare_at_price && variante.compare_at_price > variante.price) {
        html += ' <del class="chiffres">' + format(variante.compare_at_price) + '</del>';
      }
      zone.innerHTML = html;
    }

    majUrl(variante) {
      if (!variante || !this.dataset.url) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variante.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  customElements.define('selecteur-variantes', SelecteurVariantes);

  /* ------------------------------------------------------------------------
     Galerie produit
     ---------------------------------------------------------------------- */

  function initGalerie() {
    const galerie = document.querySelector('[data-galerie]');
    if (!galerie) return;

    const principale = galerie.querySelector('[data-galerie-principale]');
    const vignettes = galerie.querySelectorAll('[data-galerie-vignette]');
    if (!principale || !vignettes.length) return;

    vignettes.forEach((vignette) => {
      vignette.addEventListener('click', () => {
        principale.src = vignette.dataset.pleineTaille;
        principale.srcset = vignette.dataset.pleineSrcset || '';
        principale.alt = vignette.dataset.alt || '';

        vignettes.forEach((v) => v.setAttribute('aria-current', 'false'));
        vignette.setAttribute('aria-current', 'true');
      });
    });
  }

  /* ------------------------------------------------------------------------
     Ajout au panier
     ------------------------------------------------------------------------
     Le bouton reste désactivé pendant la requête et annonce le résultat dans
     une région aria-live, sans jamais voler le focus au visiteur.
     ---------------------------------------------------------------------- */

  function initAjoutPanier() {
    const formulaires = document.querySelectorAll('[data-formulaire-produit]');
    if (!formulaires.length) return;

    formulaires.forEach((formulaire) => {
      formulaire.addEventListener('submit', async (e) => {
        e.preventDefault();

        const bouton = formulaire.querySelector('[data-ajouter]');
        const annonce = formulaire.querySelector('[data-annonce-panier]');
        if (!bouton || bouton.disabled) return;

        bouton.classList.add('est-chargement');
        bouton.disabled = true;

        try {
          const reponse = await fetch(window.Routes.cartAdd, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(formulaire)
          });

          const resultat = await reponse.json();

          if (!reponse.ok) {
            throw new Error(resultat.description || resultat.message || 'Ajout impossible.');
          }

          await majCompteurPanier();

          if (annonce) {
            annonce.textContent = 'Ajouté au panier.';
            annonce.className = 'formulaire-message';
          }
        } catch (erreur) {
          if (annonce) {
            annonce.textContent = erreur.message + ' Vérifiez la taille choisie ou réessayez.';
            annonce.className = 'formulaire-message champ__erreur';
          }
        } finally {
          bouton.classList.remove('est-chargement');
          bouton.disabled = false;
        }
      });
    });
  }

  async function majCompteurPanier() {
    const pastille = document.querySelector('[data-compteur-panier]');
    if (!pastille) return;

    try {
      const reponse = await fetch(window.Routes.cart + '.js');
      const panier = await reponse.json();

      pastille.hidden = panier.item_count === 0;
      pastille.textContent = panier.item_count;

      const lien = pastille.closest('a');
      if (lien) {
        lien.setAttribute(
          'aria-label',
          'Panier, ' + panier.item_count + (panier.item_count > 1 ? ' articles' : ' article')
        );
      }
    } catch (e) {
      /* Le compteur reste sur sa dernière valeur connue : pas d'erreur visible
         pour une information secondaire. */
    }
  }

  /* ------------------------------------------------------------------------
     Tri des collections
     ---------------------------------------------------------------------- */

  function initTri() {
    const tri = document.querySelector('[data-tri]');
    if (!tri) return;

    tri.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', tri.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* ------------------------------------------------------------------------
     En-tête au défilement
     ---------------------------------------------------------------------- */

  function initEntete() {
    const entete = document.querySelector('[data-entete]');
    if (!entete) return;

    let enAttente = false;

    function evaluer() {
      entete.classList.toggle('est-defile', window.scrollY > 24);
      enAttente = false;
    }

    window.addEventListener(
      'scroll',
      () => {
        if (enAttente) return;
        enAttente = true;
        window.requestAnimationFrame(evaluer);
      },
      { passive: true }
    );

    evaluer();
  }

  /* ------------------------------------------------------------------------
     Amorçage
     ---------------------------------------------------------------------- */

  function demarrer() {
    initApparitions();
    initParallaxe();
    initGalerie();
    initAjoutPanier();
    initTri();
    initEntete();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }

  /* L'éditeur de thème Shopify remplace le DOM d'une section sans recharger
     la page : il faut relancer les comportements de la section concernée. */
  document.addEventListener('shopify:section:load', demarrer);
})();
